import type { FormDataAdapter } from "$lib/util"
import { normalizeFloatingPoint } from "@tastyworks/shared-utils/math/precision"
import { Applicant } from "@tastyworks/tastyworks-api"
import { number, object, string, type InferType } from "yup"

export const secondaryOwnerSchema = object({
  firstName: string().required(),
  lastName: string().required(),
  email: string().email().required(),
  percentAllocation: string()
    .default("50")
    .test(
      "less-than-100",
      "must be less than 100%",
      (value) => !value || parseFloat(value) < 100
    )
    .test(
      "greater-than-0",
      "must be > 0%",
      (value) => !value || parseFloat(value) > 0
    ),
})

export const estatePercentageAllocation = object({
  totalEstateAllocation: number()
    .required()
    .test(
      "exactly-100-percent",
      "account.additionalInformation.estatePercentageAllocation.totalEstatePercent",
      (value) => value === 100.0
    )
    .default(100),
  primaryApplicantPercent: string()
    .required()
    .default("50")
    .test(
      "less-than-100",
      "must be less than 100%",
      (value) => parseFloat(value) < 100
    )
    .test("greater-than-0", "must be > 0%", (value) => parseFloat(value) > 0),
  secondaryApplicantPercent: string()
    .required()
    .default("50")
    .test(
      "less-than-100",
      "must be less than 100%",
      (value) => parseFloat(value) < 100
    )
    .test("greater-than-0", "must be > 0%", (value) => parseFloat(value) > 0),
})

export const additionalInformationSchema = object({
  secondaryOwner: secondaryOwnerSchema,
  estatePercentageAllocation: estatePercentageAllocation,
})

export const secondaryOwnerAdapter: FormDataAdapter<
  Applicant,
  InferType<typeof secondaryOwnerSchema>
> = {
  toForm: (applicant, formData) => {
    formData.firstName = applicant.applicantData.firstName
    formData.lastName = applicant.applicantData.lastName
    formData.email = applicant.applicantData.email
    if ("confirmEmail" in formData) {
      formData.confirmEmail = applicant.applicantData.email
    }
    if ("percentAllocation" in formData) {
      formData.percentAllocation = String(
        applicant.applicantData.estatePercent * 100
      )
    }
    return formData
  },
  toModel: (formData, applicant) => {
    applicant.applicantData.firstName = formData.firstName
    applicant.applicantData.lastName = formData.lastName
    applicant.applicantData.email = formData.email

    return applicant
  },
}

export const estatePercentageAllocationAdapter: FormDataAdapter<
  [Applicant, Applicant],
  InferType<typeof estatePercentageAllocation>
> = {
  toForm: ([primary, secondary], formData) => {
    formData.totalEstateAllocation = 100
    formData.primaryApplicantPercent = String(
      primary.applicantData?.estatePercent * 100
    )
    formData.secondaryApplicantPercent = String(
      secondary.applicantData?.estatePercent * 100
    )
    return formData
  },
  toModel: (formData, [primary, secondary]) => {
    primary.applicantData.estatePercent = normalizeFloatingPoint(
      parseFloat(formData.primaryApplicantPercent ?? "") / 100,
      4
    )
    secondary.applicantData.estatePercent = normalizeFloatingPoint(
      parseFloat(formData.secondaryApplicantPercent ?? "") / 100,
      4
    )
    return [primary, secondary]
  },
}
